// frontend/src/pages/ServiceTypesManager.jsx
// Platform admin UI for managing the integration service catalog.
// Renders inside GlobalIntegrationsSettings.

import React, { useState } from 'react';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
import { LoadingSpinner, ConfirmModal } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import {
    Box, Typography, Button, Stack, Card, Avatar, Chip, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Divider, Grid, FormControl, InputLabel, Select, MenuItem,
    Switch, FormControlLabel, Tooltip,
} from '@mui/material';
import {
    Add as AddIcon, Edit, DeleteOutline, Lock as LockIcon,
    Extension as ExtensionIcon, Check as CheckIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { notify } from '@/components/core';

// ─── Color palette for quick-pick ────────────────────────────────────────────
const COLOR_PALETTE = [
    '#1a82e2', '#6366f1', '#f22f46', '#10a37f', '#f59e0b',
    '#635bff', '#06b6d4', '#8b5cf6', '#ec4899', '#64748b',
];

// ─── Empty field template ─────────────────────────────────────────────────────
const emptyField = () => ({ key: '', label: '', type: 'text', required: false });

const SERVICE_CATEGORIES = [
    { value: 'Email', label: 'Email', color: '#6366f1' },
    { value: 'SMS', label: 'SMS', color: '#f22f46' },
    { value: 'Messaging', label: 'Messaging', color: '#25d366' },
    { value: 'LLM', label: 'LLM / AI', color: '#10a37f' },
    { value: 'Payments', label: 'Payments', color: '#f59e0b' },
    { value: 'Storage', label: 'Storage', color: '#06b6d4' },
    { value: 'Collaboration', label: 'Collaboration', color: '#0078d4' },
    { value: 'Ticketing', label: 'Ticketing', color: '#0052cc' },
    { value: 'CRM', label: 'CRM', color: '#8b5cf6' },
    { value: 'Other', label: 'Other', color: '#64748b' },
];

const getCategoryColor = (cat) => SERVICE_CATEGORIES.find(c => c.value === cat)?.color || '#64748b';

// ─── ServiceTypeFormDialog ────────────────────────────────────────────────────
const ServiceTypeFormDialog = ({ open, onClose, onSave, editing }) => {
    const init = {
        service_id: editing?.service_id || '',
        label: editing?.label || '',
        icon: editing?.icon || 'Plug',
        color: editing?.color || '#6366f1',
        description: editing?.description || '',
        category: editing?.category || '',
        fields: editing?.fields?.length ? editing.fields : [emptyField()],
    };
    const [form, setForm] = useState(init);
    const [saving, setSaving] = useState(false);

    // Reset when dialog opens with new editing target
    React.useEffect(() => {
        setForm({
            service_id: editing?.service_id || '',
            label: editing?.label || '',
            icon: editing?.icon || 'Plug',
            color: editing?.color || '#6366f1',
            description: editing?.description || '',
            category: editing?.category || '',
            fields: editing?.fields?.length ? editing.fields : [emptyField()],
        });
    }, [editing, open]);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const setField = (i, k, v) =>
        setForm(p => ({ ...p, fields: p.fields.map((f, idx) => idx === i ? { ...f, [k]: v } : f) }));
    const addField = () => setForm(p => ({ ...p, fields: [...p.fields, emptyField()] }));
    const removeField = (i) => setForm(p => ({ ...p, fields: p.fields.filter((_, idx) => idx !== i) }));

    const valid = form.service_id.trim() && form.label.trim() &&
        form.fields.every(f => f.key.trim() && f.label.trim());

    const handleSave = async () => {
        if (!valid) return;
        setSaving(true);
        // Convert service_id to slug
        const payload = {
            ...form,
            service_id: form.service_id.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
        };
        await onSave(payload);
        setSaving(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold' }}>
                {editing ? `Edit ${editing.label}` : 'Add New Service Type'}
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5} sx={{ pt: 1 }}>
                    {/* Basic info row */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField label="Service ID *" size="small" fullWidth
                                value={form.service_id}
                                onChange={e => set('service_id', e.target.value)}
                                disabled={!!editing}
                                helperText={editing ? 'Cannot change after creation' : 'e.g. stripe, slack'}
                                inputProps={{ style: { fontFamily: 'monospace' } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField label="Display Label *" size="small" fullWidth
                                value={form.label}
                                onChange={e => set('label', e.target.value)}
                                placeholder="e.g. Stripe"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField label="Icon Name" size="small" fullWidth
                                value={form.icon}
                                onChange={e => set('icon', e.target.value)}
                                placeholder="e.g. CreditCard"
                                helperText="Lucide icon name"
                                InputProps={{
                                    endAdornment: (
                                        <Avatar variant="rounded" sx={{ bgcolor: `${form.color}22`, width: 28, height: 28, mr: -0.5 }}>
                                            <DynamicIcon name={form.icon} className="h-4 w-4" style={{ color: form.color }} />
                                        </Avatar>
                                    )
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                            <TextField label="Description" size="small" fullWidth
                                value={form.description}
                                onChange={e => set('description', e.target.value)}
                                placeholder="Short description shown under the service name"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select value={form.category} label="Category"
                                    onChange={e => set('category', e.target.value)}
                                    renderValue={(val) => (
                                        <Chip label={val || 'None'} size="small" sx={{
                                            height: 20, fontSize: '0.7rem', fontWeight: 700,
                                            bgcolor: val ? `${getCategoryColor(val)}18` : 'action.hover',
                                            color: val ? getCategoryColor(val) : 'text.secondary',
                                        }} />
                                    )}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {SERVICE_CATEGORIES.map(cat => (
                                        <MenuItem key={cat.value} value={cat.value}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cat.color }} />
                                                <span>{cat.label}</span>
                                            </Stack>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {/* Color picker */}
                    <Box>
                        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                            Brand Color
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {COLOR_PALETTE.map(c => (
                                <Box key={c} onClick={() => set('color', c)}
                                    sx={{
                                        width: 26, height: 26, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                                        border: form.color === c ? '3px solid white' : '2px solid transparent',
                                        boxShadow: form.color === c ? `0 0 0 2px ${c}` : 'none',
                                        transition: 'transform 0.1s',
                                        '&:hover': { transform: 'scale(1.2)' },
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                >
                                    {form.color === c && <CheckIcon sx={{ fontSize: 14, color: 'white' }} />}
                                </Box>
                            ))}
                            <TextField size="small" sx={{ width: 110 }}
                                value={form.color}
                                onChange={e => set('color', e.target.value)}
                                inputProps={{ maxLength: 7 }}
                                InputProps={{
                                    startAdornment: (
                                        <Box sx={{ width: 16, height: 16, bgcolor: form.color, borderRadius: '3px', mr: 0.5, flexShrink: 0 }} />
                                    )
                                }}
                            />
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Fields */}
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold">Credential Fields</Typography>
                            <Button size="small" startIcon={<AddIcon />} onClick={addField}>Add Field</Button>
                        </Stack>

                        <Stack spacing={1.5}>
                            {form.fields.map((field, i) => (
                                <Grid container spacing={1.5} key={i} alignItems="center">
                                    <Grid item xs={12} sm={3}>
                                        <TextField size="small" fullWidth label="Field Key *"
                                            value={field.key}
                                            onChange={e => setField(i, 'key', e.target.value)}
                                            placeholder="e.g. api_key"
                                            inputProps={{ style: { fontFamily: 'monospace' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField size="small" fullWidth label="Display Label *"
                                            value={field.label}
                                            onChange={e => setField(i, 'label', e.target.value)}
                                            placeholder="e.g. API Key"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormControl size="small" fullWidth>
                                            <InputLabel>Type</InputLabel>
                                            <Select value={field.type} label="Type"
                                                onChange={e => setField(i, 'type', e.target.value)}>
                                                <MenuItem value="text">Text</MenuItem>
                                                <MenuItem value="password">Password (masked)</MenuItem>
                                                <MenuItem value="email">Email</MenuItem>
                                                <MenuItem value="number">Number</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={8} sm={2}>
                                        <FormControlLabel
                                            control={<Switch size="small" checked={field.required}
                                                onChange={e => setField(i, 'required', e.target.checked)} />}
                                            label={<Typography variant="caption">Required</Typography>}
                                        />
                                    </Grid>
                                    <Grid item xs={4} sm={1}>
                                        <IconButton size="small" color="error"
                                            onClick={() => removeField(i)}
                                            disabled={form.fields.length === 1}>
                                            <DeleteOutline fontSize="small" />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={!valid || saving}>
                    {editing ? 'Update Service' : 'Create Service'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Main Export: ServiceTypesManager ────────────────────────────────────────
const ServiceTypesManager = ({ token, embedded }) => {
    const theme = useTheme();
    const { data: serviceTypes, isLoading, refresh } = useData('/integrations/platform/service-types', token);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [confirmDel, setConfirmDel] = useState(null);

    const handleSave = async (payload) => {
        try {
            if (editing) {
                await apiFetch(`/integrations/platform/service-types/${editing.id}`, token, {
                    method: 'PUT', body: JSON.stringify(payload)
                });
                notify.success('Service type updated.');
            } else {
                await apiFetch('/integrations/platform/service-types', token, {
                    method: 'POST', body: JSON.stringify(payload)
                });
                notify.success('Service type created.');
            }
            refresh();
        } catch (e) { notify.error(e.message || 'Failed.'); }
    };

    const handleDelete = async () => {
        if (!confirmDel) return;
        try {
            await apiFetch(`/integrations/platform/service-types/${confirmDel.id}`, token, { method: 'DELETE' });
            notify.success('Service type deleted.');
            refresh();
        } catch (e) { notify.error(e.message || 'Failed.'); }
        finally { setConfirmDel(null); }
    };

    return (
        <Box sx={{ mb: embedded ? 0 : 4 }}>
            <Stack direction="row" justifyContent={embedded ? 'flex-end' : 'space-between'} alignItems="center" sx={{ mb: 2 }}>
                {!embedded && (
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <ExtensionIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle1" fontWeight="bold">Manage Service Types</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            Define the integration services available to all tenants. Built-in services are read-only.
                        </Typography>
                    </Box>
                )}
                <Button variant={embedded ? 'contained' : 'outlined'} startIcon={<AddIcon />}
                    onClick={() => { setEditing(null); setFormOpen(true); }}>
                    Add Service
                </Button>
            </Stack>

            {isLoading ? <LoadingSpinner /> : (
                <Grid container spacing={2}>
                    {(serviceTypes || []).map(svc => (
                        <Grid item xs={12} sm={6} md={4} key={svc.service_id || svc.id}>
                            <Card elevation={0} sx={{
                                border: '1px solid', borderColor: 'divider', borderRadius: 2,
                                opacity: svc.is_active === false ? 0.5 : 1,
                                transition: 'all 0.2s',
                                '&:hover': { boxShadow: theme.shadows[3], borderColor: svc.color || 'divider' },
                            }}>
                                <Box sx={{ p: 2 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <Avatar variant="rounded" sx={{ bgcolor: `${svc.color}18`, width: 44, height: 44, flexShrink: 0 }}>
                                            <DynamicIcon name={svc.icon} className="h-5 w-5" style={{ color: svc.color }} />
                                        </Avatar>
                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="subtitle2" fontWeight="bold" noWrap>{svc.label}</Typography>
                                                {svc.built_in && (
                                                    <Tooltip title="Built-in service — cannot be deleted">
                                                        <Chip icon={<LockIcon sx={{ fontSize: '10px !important' }} />}
                                                            label="Built-in" size="small"
                                                            sx={{ height: 18, fontSize: '0.6rem', pl: 0.3 }} />
                                                    </Tooltip>
                                                )}
                                                {svc.category && (
                                                    <Chip label={svc.category} size="small" sx={{
                                                        height: 18, fontSize: '0.6rem', fontWeight: 700,
                                                        bgcolor: `${getCategoryColor(svc.category)}18`,
                                                        color: getCategoryColor(svc.category),
                                                    }} />
                                                )}
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                {svc.description || '—'}
                                            </Typography>
                                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                                                {(svc.fields || []).map(f => (
                                                    <Chip key={f.key} label={f.key}
                                                        size="small"
                                                        sx={{
                                                            height: 18, fontSize: '0.6rem', fontFamily: 'monospace',
                                                            bgcolor: f.required ? `${svc.color}18` : 'action.hover',
                                                            color: f.required ? svc.color : 'text.secondary'
                                                        }} />
                                                ))}
                                            </Stack>
                                        </Box>
                                        {!svc.built_in && (
                                            <Stack>
                                                <IconButton size="small" onClick={() => { setEditing(svc); setFormOpen(true); }}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => setConfirmDel(svc)}>
                                                    <DeleteOutline fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        )}
                                    </Stack>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            <ServiceTypeFormDialog
                open={formOpen}
                onClose={() => { setFormOpen(false); setEditing(null); }}
                onSave={handleSave}
                editing={editing}
            />
            <ConfirmModal isOpen={!!confirmDel} onClose={() => setConfirmDel(null)} onConfirm={handleDelete}
                title={`Delete "${confirmDel?.label}"?`}>
                All keys using this service type will still exist but the service won&apos;t appear in the picker.
            </ConfirmModal>
        </Box>
    );
};

export default ServiceTypesManager;
